import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode, useEffect, useMemo, useRef } from 'react'
import { SafeAreaProvider } from 'react-native-safe-area-context'

import { FunkitBrandingProvider } from '../branding/BrandingContext'
import type { BackdropBlurRenderer } from '../flow/FlowSheet'
import type { StripeOnrampConfig } from '../fiat/providers/useStripeOnrampClient'
import { FunkitFlowProvider } from '../flow/FunkitFlowProvider'
import { useFrozenIdentity } from './useFrozenIdentity'
import {
  FunkitHapticsProvider,
  type HapticFeedback,
} from '../haptics/HapticsContext'
import {
  type FunkitConfig,
  FunkitIdentityProvider,
  resolveFunkitIdentity,
} from '../identity/IdentityContext'
import type { Locale } from '../locales/i18n'
import { logger } from '../logging/logger'
import {
  FunkitThemeProvider,
  type FunkitThemeProviderProps,
} from '../theme/FunkitThemeProvider'
import type { ClipboardReader, ClipboardWriter } from '../transfer/clipboard'
import { CONNECT_RN_VERSION } from '../version'
import { FunkitI18nProvider } from './FunkitI18nProvider'
import { FunkitStatsigProvider } from './FunkitStatsigProvider'

export interface FunkitProviderProps {
  children: ReactNode
  /**
   * Identity config — `apiKey` (+ optional `externalUserId`). Mirrors the
   * identity-relevant subset of the web SDK's `funkitConfig`. Required for the
   * SDK to generate deposit addresses; when omitted, the deposit flow falls
   * back to any host-supplied `depositAddress` on the flow config.
   */
  config?: FunkitConfig
  /**
   * The connected wallet address (default transfer recipient). On web this
   * comes from wagmi; RN has no wallet provider, so the host passes it here.
   *
   * Together with `config` this is who is paying, and it holds still while a
   * flow is open: a change made mid-flow is logged and applies on close.
   */
  walletAddress?: string
  /**
   * React Query client. Web requires the host to supply one; for single-provider
   * RN ergonomics we default to an internally-created client when omitted (pass
   * your own to share a cache with the rest of the app).
   */
  queryClient?: QueryClient
  /**
   * Light/dark Restyle theme set. Defaults to the built-in Fun Mobile themes.
   * Forwarded to {@link FunkitThemeProvider}.
   */
  themes?: FunkitThemeProviderProps['themes']
  /**
   * Optional forced color scheme. When set, the scheme is controlled (system
   * Appearance ignored, `toggleColorScheme()` disabled); when omitted it
   * follows the system Appearance and is toggleable. Forwarded to
   * {@link FunkitThemeProvider}.
   */
  colorScheme?: FunkitThemeProviderProps['colorScheme']
  /**
   * Run Statsig in development mode (dev environment tier + verbose logging).
   * Mirrors `@funkit/connect`'s `debug`.
   */
  debug?: boolean
  /**
   * UI language for the SDK's own copy (defaults to English). Mirrors
   * `@funkit/connect`'s `locale`.
   */
  locale?: Locale
  /**
   * Clipboard write used by the deposit flow's "Copy address" buttons. Defaults
   * to the bare-RN `@react-native-clipboard/clipboard` peer (lazily loaded).
   * Inject your own to use a different module — e.g. pass `setStringAsync` from
   * `expo-clipboard` when running in **Expo Go**, where the native module isn't
   * linked. Return the write's success to drive the "Copied!" confirmation; a
   * `void` return is treated as success.
   */
  copyToClipboard?: ClipboardWriter
  /**
   * Clipboard read behind the withdrawal's "Paste" button. Defaults to the same
   * bare-RN peer as {@link FunkitProviderProps.copyToClipboard} — inject your own
   * (e.g. `getStringAsync` from `expo-clipboard`) where that module isn't linked.
   */
  pasteFromClipboard?: ClipboardReader
  /**
   * Replaces the material under the flow sheets. The SDK ships its own — a
   * blur of the host app with iOS 26 Liquid Glass through it — so leave this
   * unset unless the app wants a different native material.
   */
  renderBackdropBlur?: BackdropBlurRenderer
  /**
   * Set with `renderBackdropBlur` when that view is iOS 26 Liquid Glass. The
   * sheet then paints no fill over it, so the glass refracts the host app
   * instead of a flat panel. Meaningless alone.
   */
  liquidGlass?: boolean
  /**
   * Turns the Stripe fiat rail on. This package bundles Stripe's native SDK and
   * drives it itself; all a host supplies is its own account's publishable key,
   * the display name for Stripe's sheets, and — for Apple Pay — the Apple
   * Merchant ID from its entitlements. Omit it and no Stripe code runs.
   */
  stripeOnramp?: StripeOnrampConfig
  /**
   * Tactile tap for the withdrawal keypad. Omitted, the SDK taps through the
   * optional `react-native-haptic-feedback` peer when it is installed, and
   * stays silent when it isn't. Pass your own to use another module —
   * `expo-haptics` in Expo Go, which links only Expo's own — or a no-op for
   * silence.
   */
  hapticFeedback?: HapticFeedback
}

// Sentinel identity used when the host mounts the provider without a `config`.
// `'0x'` userId/address disables the transfer query (getCheckoutTokenTransferParams
// returns undefined), so the flow shows its fallback rather than firing a bad
// request.
const ANONYMOUS_CONFIG: FunkitConfig = { apiKey: '' }

// Metro's debug-build global; `typeof`-probed so non-RN runtimes (tests, SSR)
// resolve to false. Declared locally — this tsconfig loads only node types.
declare const __DEV__: boolean | undefined
const IS_DEV_BUILD = typeof __DEV__ !== 'undefined' && !!__DEV__

// Conservative default for the internally-created fallback client. Stock
// react-query staleTime of 0 marks data stale immediately, so every remount
// refetches and needlessly hits the API on mobile; a 60s floor stops that.
// Retry is intentionally left at the react-query default — lowering it would
// weaken flaky-network resilience (the case this SDK most needs to survive) and
// can make the geo-compliance lookup fail closed. Hooks that need fresher or
// staler data set their own staleTime.
function createFallbackQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
      },
    },
  })
}

/**
 * Top-level, user-facing provider for `@funkit/connect-rn` — the single
 * provider host apps mount around their tree.
 *
 * Composes the platform halves, mirroring the web `FunkitProvider` nesting
 * (Statsig flags → React Query → identity → branding → theme → i18n → the
 * managed flow). Statsig stays outermost so flags are available everywhere;
 * the deposit-flow query hooks below it read identity from
 * {@link FunkitIdentityProvider} and run on the {@link QueryClientProvider}.
 * Host integration stays a single
 * `<FunkitProvider>`.
 */
export function FunkitProvider({
  children,
  config,
  walletAddress,
  queryClient,
  themes,
  colorScheme,
  debug = IS_DEV_BUILD,
  locale,
  copyToClipboard,
  pasteFromClipboard,
  renderBackdropBlur,
  liquidGlass,
  stripeOnramp,
  hapticFeedback,
}: FunkitProviderProps) {
  // Stable default client when the host doesn't supply one.
  const fallbackClient = useRef<QueryClient>(undefined)
  fallbackClient.current ??= createFallbackQueryClient()
  const client = queryClient ?? fallbackClient.current

  const { identity: identityProps, onFlowOpenChange } = useFrozenIdentity({
    config: config ?? ANONYMOUS_CONFIG,
    walletAddress,
  })
  const identity = useMemo(
    () =>
      resolveFunkitIdentity(identityProps.config, identityProps.walletAddress),
    [identityProps],
  )

  // Configure the Datadog logger once the apiKey is known. Without this the
  // FunLogger stays unconfigured, queues every record, and never emits to the
  // transport. Mirrors the web FunkitProvider. Only configure when an apiKey is
  // present — configuring with none makes every log a console "apiKey not set"
  // error, and there's nothing to ship to Datadog anyway.
  useEffect(() => {
    if (!identity.apiKey) {
      return
    }
    logger.configure(identity.apiKey, debug, CONNECT_RN_VERSION)
  }, [identity.apiKey, debug])

  // Attach user context to every log (no apiKey required).
  useEffect(() => {
    logger.setUserInfo(
      identity.userId,
      identity.walletAddress,
      '',
      identity.loginType ?? '',
      '',
    )
  }, [identity])

  return (
    <SafeAreaProvider>
      <FunkitStatsigProvider
        apiKey={identity.apiKey}
        userId={identity.userId}
        isDevelopment={debug}
      >
        <QueryClientProvider client={client}>
          <FunkitIdentityProvider identity={identity}>
            <FunkitBrandingProvider hideAttribution={config?.hideAttribution}>
              <FunkitThemeProvider themes={themes} colorScheme={colorScheme}>
                <FunkitI18nProvider locale={locale}>
                  <FunkitHapticsProvider hapticFeedback={hapticFeedback}>
                    <FunkitFlowProvider
                      copyToClipboard={copyToClipboard}
                      pasteFromClipboard={pasteFromClipboard}
                      renderBackdropBlur={renderBackdropBlur}
                      {...(liquidGlass === undefined ? {} : { liquidGlass })}
                      stripeOnramp={stripeOnramp}
                      onOpenChange={onFlowOpenChange}
                    >
                      {children}
                    </FunkitFlowProvider>
                  </FunkitHapticsProvider>
                </FunkitI18nProvider>
              </FunkitThemeProvider>
            </FunkitBrandingProvider>
          </FunkitIdentityProvider>
        </QueryClientProvider>
      </FunkitStatsigProvider>
    </SafeAreaProvider>
  )
}
